import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, CircularProgress, Tabs } from "@mui/material";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import {
  actionProposalChangeRequest,
  bulkUpsertChangeRequestMilestones,
  getChangeRequestPaymentPlan,
  getProjectBillingMilestones,
  getProposalChangeRequest,
  previewProposalChangeRequest,
  saveChangeRequestPaymentPlan,
  upsertChangeRequestMilestone,
} from "../../services/contractorService";
import { StatusPill } from "../ContractorProjectDetails/components/Changes";
import BillingMilestoneTab from "./components/BillingMilestoneTab";
import TimelineTab from "./components/TimelineTab";
import PaymentsTab from "./components/PaymentsTab";
import PreviewTab from "./components/PreviewTab";
import {
  PrimaryButton,
  SecondaryButton,
  StyledTab,
} from "./components/styled";
import {
  PAYMENT_LINE_CURRENCY,
  PAYMENT_LINE_STATUS,
  PAYMENT_LINE_TYPE,
  TABS,
  deliverablesPayload,
  isInitialDeposit,
  isMilestoneDirty,
  isoDay,
  milestoneAmountOf,
  milestoneDueDateOf,
  milestoneGroupUuid,
  toEditableDeliverables,
} from "./components/helpers";
import SubmitChangeRequestModal from "./modals/SubmitChangeRequestModal";

// SPLIT strategy is now the only supported payment structure. The two
// lines are seeded from a percentage; amount is recomputed from the
// CR price at save time.
const todayIso = () => new Date().toISOString().slice(0, 10);
const seedSplitLines = (price) => [
  {
    id: null,
    displayName: "Downpayment",
    percentage: "50",
    amount: price ? String((Number(price) || 0) / 2) : "",
    dueDate: todayIso(),
  },
  {
    id: null,
    displayName: "Final Payment",
    percentage: "50",
    amount: price ? String((Number(price) || 0) / 2) : "",
    dueDate: "",
  },
];

export default function ChangeRequest() {
  const { id, crId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const projectId = searchParams.get("project_id") || id;
  const proposalId = id;

  const [tabIndex, setTabIndex] = useState(0);

  const [loading, setLoading] = useState(false);
  const [cr, setCr] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [milestones, setMilestones] = useState([]);

  // Editable per-milestone map. Map keyed by milestone id.
  // Shape: { [milestoneId]: { amount, dueDate, deliverables, expanded,
  //                           hasSavedChange } }
  const [editedById, setEditedById] = useState({});
  // Multi-select: the user can tick any number of rows. Each ticked
  // milestone whose edit differs from base is considered dirty and
  // gets POSTed on Next.
  const [selectedMilestoneIds, setSelectedMilestoneIds] = useState(
    () => new Set(),
  );
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

  // Synthetic "Change Request Milestone" — a virtual row that
  // represents the CR's new milestone. The amount seeds from the CR
  // price but is editable; name / due date / deliverables are user-
  // driven. `name` defaults to a placeholder but the user can rename.
  const [syntheticEdit, setSyntheticEdit] = useState({
    name: "Change Request Milestone",
    amount: "",
    dueDate: "",
    deliverables: [],
    // Synthetic row is always "checked" so its accordion opens by
    // default — the user lands with the new milestone's deliverables
    // immediately accessible.
    expanded: true,
  });

  // Seed the synthetic amount from the CR price once it loads. Only
  // overwrites when the user hasn't already typed something.
  useEffect(() => {
    if (cr?.changeRequestPrice == null) return;
    setSyntheticEdit((prev) =>
      prev.amount === "" || prev.amount == null
        ? { ...prev, amount: String(cr.changeRequestPrice) }
        : prev,
    );
  }, [cr?.changeRequestPrice]);

  // Payments tab — always SPLIT now. Two lines seeded from the CR
  // price; the user adjusts percentages + due dates and the saved
  // payload always carries strategy: "SPLIT".
  const [paymentLines, setPaymentLines] = useState(() => seedSplitLines(0));
  const [savingPaymentPlan, setSavingPaymentPlan] = useState(false);
  const [paymentPlanHydrated, setPaymentPlanHydrated] = useState(false);
  const [paymentErrors, setPaymentErrors] = useState(null);

  // Reset error highlights whenever the user touches the payments form.
  const setPaymentLinesAndClearErrors = (updater) => {
    setPaymentErrors(null);
    setPaymentLines(updater);
  };

  // Re-seed split lines once the CR price loads if the user hasn't
  // already touched a saved plan.
  useEffect(() => {
    if (paymentPlanHydrated) return;
    if (cr?.changeRequestPrice == null) return;
    setPaymentLines(seedSplitLines(cr.changeRequestPrice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cr?.changeRequestPrice, paymentPlanHydrated]);

  const isReadOnly = useMemo(() => {
    const s = String(cr?.status || "").toUpperCase();
    return s && s !== "DRAFT";
  }, [cr?.status]);

  // Toggle the checkbox for a milestone — purely additive/subtractive,
  // no warning modal. Removing a ticked milestone leaves the edited
  // values in `editedById` untouched so re-ticking restores them.
  const handleToggleMilestone = (id) => {
    setSelectedMilestoneIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Auto-tick a row when the user begins editing any of its fields.
  // Idempotent — calling on an already-selected row is a no-op.
  const handleAutoSelectMilestone = (id) => {
    setSelectedMilestoneIds((prev) => {
      const key = String(id);
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  // Milestones that currently have an edit differing from the base.
  // Drives Next gating + chooses between single and bulk save.
  const dirtyMilestones = useMemo(() => {
    return [...selectedMilestoneIds]
      .map((sid) => ({
        milestone:
          milestones.find((m) => String(m.id) === String(sid)) || null,
        edit: editedById[String(sid)],
      }))
      .filter(({ milestone, edit }) => isMilestoneDirty(milestone, edit));
  }, [selectedMilestoneIds, editedById, milestones]);

  const anyDirty = dirtyMilestones.length > 0;

  // Synthetic row is "dirty" once the user has filled any of its
  // mutable fields. The amount auto-seeds to CR price, so we count it
  // as dirty if it differs from the CR price OR a due date / new
  // deliverable was added.
  const isSyntheticDirty = useMemo(() => {
    const seedAmt = String(cr?.changeRequestPrice ?? "");
    const curAmt = String(syntheticEdit?.amount ?? "");
    if (curAmt !== seedAmt) return true;
    if (syntheticEdit?.dueDate) return true;
    if ((syntheticEdit?.deliverables || []).length > 0) return true;
    return false;
  }, [syntheticEdit, cr?.changeRequestPrice]);

  // Tabs unlock once any milestone (existing or synthetic) is dirty
  // OR the CR is read-only (viewer mode).
  const tabsUnlocked = isReadOnly || anyDirty || isSyntheticDirty;

  // Timeline tab consumes every deliverable in scope for the CR —
  // both the synthetic row's deliverables and any checked milestone's
  // deliverables. Each entry is tagged with its `_source` (synthetic
  // or milestone id) so edits route back to the right slice of state.
  const timelineDeliverables = useMemo(() => {
    const rows = [];
    (syntheticEdit?.deliverables || []).forEach((d, idx) => {
      rows.push({ ...d, _source: "__synthetic__", _idx: idx });
    });
    [...selectedMilestoneIds].forEach((sid) => {
      const dels = editedById[String(sid)]?.deliverables || [];
      dels.forEach((d, idx) => {
        rows.push({ ...d, _source: String(sid), _idx: idx });
      });
    });
    return rows;
  }, [syntheticEdit?.deliverables, editedById, selectedMilestoneIds]);

  const setTimelineDeliverables = (updater) => {
    // The Timeline tab calls us with an updater that returns the new
    // flat array. We diff it back into the original sources.
    const nextFlat =
      typeof updater === "function"
        ? updater(timelineDeliverables)
        : updater;
    // Group by source.
    const bySource = {};
    nextFlat.forEach((d) => {
      const src = d._source || "__synthetic__";
      if (!bySource[src]) bySource[src] = [];
      // Strip the routing keys before writing back.
      const { _source, _idx, ...rest } = d;
      bySource[src].push(rest);
    });

    // Synthetic
    setSyntheticEdit((prev) => ({
      ...(prev || {}),
      deliverables: bySource["__synthetic__"] || prev?.deliverables || [],
    }));
    // Each milestone
    setEditedById((prev) => {
      const next = { ...prev };
      Object.keys(bySource).forEach((key) => {
        if (key === "__synthetic__") return;
        const cur = next[key] || {};
        next[key] = { ...cur, deliverables: bySource[key] };
      });
      return next;
    });
  };

  // ---------- Data loading ----------
  const loadAll = useCallback(
    async ({ silent = false } = {}) => {
      if (!proposalId || !crId) return;
      if (!silent) setLoading(true);
      try {
        const [crRes, msRes] = await Promise.all([
          getProposalChangeRequest(proposalId, crId).catch(() => null),
          getProjectBillingMilestones(projectId).catch(() => null),
        ]);
        const crData = crRes?.data || crRes || null;
        if (crData) setCr(crData);
        // After any reload the preview is stale.
        setPreview(null);
        const list = Array.isArray(msRes?.data)
          ? msRes.data
          : Array.isArray(msRes)
          ? msRes
          : [];
        setMilestones(list);

        // Seed the editable per-milestone map. For each milestone we
        // start with its current (base) values; if the CR's snapshot
        // already overrides this milestone, we layer the snapshot on
        // top, set `hasSavedChange` so the delete button can show, and
        // auto-tick + auto-expand the row.
        const snapshot = (() => {
          const raw = crData?.snapshot;
          if (!raw) return null;
          if (typeof raw === "string") {
            try {
              return JSON.parse(raw);
            } catch {
              return null;
            }
          }
          return raw;
        })();
        const snapById = {};
        let syntheticSnap = null;
        if (Array.isArray(snapshot?.billingMilestones)) {
          snapshot.billingMilestones.forEach((s) => {
            if (s?.paymentGroupId != null) {
              // Existing milestone — key by paymentGroupId so we can
              // re-attach to the milestone row on load.
              snapById[String(s.paymentGroupId)] = s;
            } else {
              // No paymentGroupId means this snapshot entry is the
              // synthetic Change-Requests-Milestone row.
              syntheticSnap = s;
            }
          });
        }
        const seeded = {};
        const preSelected = new Set();
        list.forEach((m) => {
          const snap = snapById[String(m.id)];
          const baseAmount = String(milestoneAmountOf(m) ?? "");
          const baseDue = isoDay(milestoneDueDateOf(m));
          if (snap) preSelected.add(String(m.id));
          seeded[String(m.id)] = {
            amount:
              snap?.proposedAmount != null
                ? String(snap.proposedAmount)
                : baseAmount,
            dueDate: snap?.proposedDueDate
              ? isoDay(snap.proposedDueDate)
              : baseDue,
            deliverables: snap?.deliverables
              ? toEditableDeliverables(snap.deliverables)
              : toEditableDeliverables(m.deliverables),
            expanded: !!snap,
            hasSavedChange: !!snap,
          };
        });
        setEditedById(seeded);
        setSelectedMilestoneIds(preSelected);

        // Hydrate synthetic row from snapshot if present, otherwise
        // leave the default seeding (which will be filled from CR
        // price via the `cr?.changeRequestPrice` effect).
        if (syntheticSnap) {
          setSyntheticEdit({
            name:
              syntheticSnap.groupName ||
              syntheticSnap.group_name ||
              "Change Request Milestone",
            amount:
              syntheticSnap.proposedAmount != null
                ? String(syntheticSnap.proposedAmount)
                : "",
            dueDate: syntheticSnap.proposedDueDate
              ? isoDay(syntheticSnap.proposedDueDate)
              : "",
            deliverables: syntheticSnap.deliverables
              ? toEditableDeliverables(syntheticSnap.deliverables)
              : [],
            // Synthetic is the new CR row and is always "checked", so
            // keep its accordion open by default.
            expanded: true,
          });
        }
      } catch (e) {
        console.error("Failed to load change request", e);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [proposalId, crId, projectId],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Hydrate payment plan from server. 404 (no plan saved yet) is
  // expected, so we swallow the error and leave the seeded defaults.
  // The UI is locked to the SPLIT strategy — the GET only contributes
  // displayName / dueDate / id / percentage back into the two lines.
  const loadPaymentPlan = useCallback(async () => {
    if (!proposalId || !crId) return;
    try {
      const res = await getChangeRequestPaymentPlan(proposalId, crId);
      const plan = res?.data || res || null;
      if (!plan || !Array.isArray(plan.paymentSchedule)) return;
      const price = Number(cr?.changeRequestPrice || 0);
      const schedule = plan.paymentSchedule.slice(0, 2);
      const nextLines = schedule.map((p, idx) => {
        const amt = p?.amount != null ? Number(p.amount) : 0;
        const pctFromAmt =
          price !== 0 ? Number(((amt / price) * 100).toFixed(2)) : 50;
        return {
          id: p?.id ?? null,
          displayName:
            p?.displayName || (idx === 0 ? "Downpayment" : "Final Payment"),
          amount: String(amt),
          percentage: String(pctFromAmt),
          dueDate: p?.dueDate ? isoDay(p.dueDate) : "",
        };
      });
      // Ensure we always have exactly two lines for SPLIT.
      while (nextLines.length < 2) {
        nextLines.push({
          id: null,
          displayName: nextLines.length === 0 ? "Downpayment" : "Final Payment",
          amount: "",
          percentage: "50",
          dueDate: nextLines.length === 0 ? todayIso() : "",
        });
      }
      setPaymentLines(nextLines);
    } catch (e) {
      // 404 is the most common case here (no plan saved yet); we
      // intentionally fall back to whatever was already seeded.
    } finally {
      setPaymentPlanHydrated(true);
    }
  }, [proposalId, crId, cr?.changeRequestPrice]);

  // Defer the payment-plan GET until the user actually opens the
  // Payments tab — no point hitting the API while they're still on
  // Billing Milestone / Timeline. Fires once per session.
  useEffect(() => {
    const active = TABS[tabIndex]?.id;
    if (active !== "payments") return;
    if (paymentPlanHydrated) return;
    loadPaymentPlan();
  }, [tabIndex, paymentPlanHydrated, loadPaymentPlan]);

  const loadPreview = useCallback(async () => {
    if (!proposalId || !crId) return;
    setPreviewLoading(true);
    try {
      const res = await previewProposalChangeRequest(proposalId, crId);
      setPreview(res?.data || res || null);
    } catch (e) {
      console.error("Failed to load preview", e);
    } finally {
      setPreviewLoading(false);
    }
  }, [proposalId, crId]);

  useEffect(() => {
    const active = TABS[tabIndex]?.id;
    if (active === "preview" && !preview && !previewLoading) {
      loadPreview();
    }
  }, [tabIndex, preview, previewLoading, loadPreview]);

  // Build the payload for a single milestone change. Reused by both
  // the single and bulk POST flows. The milestone object itself
  // carries no status field — status lives only inside the
  // deliverables JSON (per deliverable + per sub-activity).
  const buildMilestonePayload = (milestone, edit) => {
    const baseAmount = milestoneAmountOf(milestone);
    const baseDue = milestoneDueDateOf(milestone);
    const proposedAmount =
      edit?.amount === "" || edit?.amount == null || edit?.amount === "-"
        ? Number(baseAmount || 0)
        : Number(edit.amount);
    const proposedDueDate = edit?.dueDate
      ? new Date(edit.dueDate).toISOString()
      : baseDue
      ? new Date(baseDue).toISOString()
      : null;
    const dels = deliverablesPayload(edit?.deliverables);
    return {
      paymentGroupId: Number(milestone.id),
      groupId: milestoneGroupUuid(milestone),
      groupName:
        milestone?.description ||
        milestone?.name ||
        milestone?.groupName ||
        milestone?.group_name ||
        "",
      proposedAmount,
      proposedDueDate,
      originalAmount: Number(baseAmount || 0),
      originalDueDate: baseDue ? new Date(baseDue).toISOString() : null,
      deliverables: JSON.stringify(dels),
    };
  };

  // Synthetic "Change Request Milestone" — a brand-new line carried
  // by the CR. Since it has no existing counterpart, we omit the
  // paymentGroupId / groupId / originalAmount / originalDueDate
  // fields entirely and only send the proposed values (the amount
  // defaults to the CR price; the user can override it).
  const buildSyntheticMilestonePayload = (synthetic) => {
    const amt =
      synthetic?.amount === "" ||
      synthetic?.amount == null ||
      synthetic?.amount === "-"
        ? 0
        : Number(synthetic.amount);
    const dels = deliverablesPayload(synthetic?.deliverables);
    return {
      groupName: synthetic?.name?.trim() || "Change Request Milestone",
      proposedAmount: amt,
      proposedDueDate: synthetic?.dueDate
        ? new Date(synthetic.dueDate).toISOString()
        : null,
      deliverables: JSON.stringify(dels),
    };
  };

  // Validate that the sum of all milestone amounts (synthetic + every
  // existing milestone's current value) equals the new proposal cost
  // — i.e. original cost + CR price. The synthetic amount soaks up
  // any residual change that the user didn't allocate to an existing
  // milestone, so a balanced ledger is required before save.
  const validateMilestoneTotals = () => {
    const baseTotal = (milestones || [])
      .filter((m) => !isInitialDeposit(m))
      .reduce((sum, m) => sum + Number(milestoneAmountOf(m) || 0), 0);
    const newProposalCost = baseTotal + Number(cr?.changeRequestPrice || 0);

    const currentSum =
      (milestones || [])
        .filter((m) => !isInitialDeposit(m))
        .reduce((sum, m) => {
          const e = editedById[String(m.id)];
          const raw = e?.amount;
          const amt =
            raw !== undefined && raw !== "" && raw !== "-"
              ? Number(raw)
              : Number(milestoneAmountOf(m) || 0);
          return sum + (Number.isFinite(amt) ? amt : 0);
        }, 0) +
      (syntheticEdit?.amount === "" ||
      syntheticEdit?.amount == null ||
      syntheticEdit?.amount === "-"
        ? 0
        : Number(syntheticEdit.amount) || 0);

    if (Math.abs(currentSum - newProposalCost) > 0.01) {
      return `Milestone amounts must sum to the new proposal cost (${newProposalCost.toFixed(
        2,
      )}). Currently ${currentSum.toFixed(2)}.`;
    }
    return null;
  };

  // ---------- Save milestone changes (DRAFT only) ----------
  // Builds a single payload entry per "milestone in play":
  //   • the synthetic Change-Requests-Milestone row (always, status ADDED)
  //   • every checked existing milestone (status MODIFIED if dirty, else ADDED)
  // Dispatches to the single endpoint when exactly one entry is in
  // the payload; otherwise hits the /bulk endpoint with the array.
  const handleSaveMilestoneAndNext = async () => {
    if (!anyDirty && !isSyntheticDirty) {
      toast.error(
        "Tick a milestone and change at least one of amount / due date / deliverables",
      );
      return;
    }
    // The synthetic Change Requests Milestone must carry a name.
    if (!syntheticEdit?.name || !syntheticEdit.name.trim()) {
      toast.error("Add a milestone name");
      return;
    }
    const totalsError = validateMilestoneTotals();
    if (totalsError) {
      toast.error(totalsError);
      return;
    }

    // Build the full payload list.
    const payloadArray = [buildSyntheticMilestonePayload(syntheticEdit)];
    [...selectedMilestoneIds].forEach((sid) => {
      const m = milestones.find((x) => String(x.id) === String(sid));
      if (!m) return;
      const edit = editedById[String(sid)];
      payloadArray.push(buildMilestonePayload(m, edit));
    });

    setSaving(true);
    try {
      if (payloadArray.length === 1) {
        await upsertChangeRequestMilestone(
          proposalId,
          crId,
          payloadArray[0],
        );
      } else {
        await bulkUpsertChangeRequestMilestones(
          proposalId,
          crId,
          payloadArray,
        );
      }
      await loadAll({ silent: true });
      setTabIndex(1);
    } catch (e) {
      console.error("Failed to save milestone change(s)", e);
      toast.error(e?.message || "Failed to save milestone changes");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Payments tab — save plan + next (DRAFT only) ----------
  // Always sends `strategy: "SPLIT"` with exactly two lines. Each
  // line's amount is derived from its percentage so the running total
  // is guaranteed to equal the CR price.
  const buildPaymentPlanPayload = () => {
    const price = Number(cr?.changeRequestPrice || 0);
    const amountForLine = (l) =>
      Number(((Number(l?.percentage || 0) / 100) * price).toFixed(2));
    const linesPayload = (paymentLines || []).map((l, idx) => ({
      id: l?.id ?? null,
      planId: 5,
      projectId: projectId ? Number(projectId) : null,
      displayName:
        l?.displayName?.trim() ||
        (idx === 0 ? "Downpayment" : "Final Payment"),
      type: PAYMENT_LINE_TYPE,
      amount: amountForLine(l),
      currency: PAYMENT_LINE_CURRENCY,
      dueDate: l?.dueDate || null,
      status: PAYMENT_LINE_STATUS,
    }));
    return { strategy: "SPLIT", paymentLines: linesPayload };
  };

  // SPLIT validation — every line needs a name and due date, and the
  // two percentages must sum to 100.
  const validatePaymentPlan = () => {
    const rowErrors = {};
    const flag = (idx, field) => {
      if (!rowErrors[idx]) rowErrors[idx] = {};
      rowErrors[idx][field] = true;
    };
    const wrap = (message) =>
      message
        ? { message, fieldErrors: { rows: rowErrors } }
        : { message: null, fieldErrors: null };

    let msg = null;
    for (let i = 0; i < (paymentLines || []).length; i++) {
      const l = paymentLines[i];
      if (!l?.displayName?.trim()) {
        flag(i, "displayName");
        msg = msg || `Enter a payment name for payment ${i + 1}`;
      }
      if (!l?.dueDate) {
        flag(i, "dueDate");
        msg = msg || `Select a due date for payment ${i + 1}`;
      }
      if (l?.percentage === "" || l?.percentage == null) {
        flag(i, "percentage");
        msg = msg || `Enter a percentage for payment ${i + 1}`;
      }
    }
    if (msg) return wrap(msg);
    const totalPct = (paymentLines || []).reduce(
      (sum, l) => sum + Number(l?.percentage || 0),
      0,
    );
    if (Math.abs(totalPct - 100) > 0.01) {
      (paymentLines || []).forEach((_, i) => flag(i, "percentage"));
      return wrap("Percentages must add up to 100%");
    }
    return wrap(null);
  };

  const handleSavePaymentPlanAndNext = async () => {
    if (isReadOnly) {
      setTabIndex(3);
      return;
    }
    const { message, fieldErrors } = validatePaymentPlan();
    if (message) {
      setPaymentErrors(fieldErrors);
      toast.error(message);
      return;
    }
    setPaymentErrors(null);
    setSavingPaymentPlan(true);
    try {
      await saveChangeRequestPaymentPlan(
        proposalId,
        crId,
        buildPaymentPlanPayload(),
      );
      // Refresh the preview so the Preview tab renders with the
      // just-saved payment plan instead of a stale snapshot. We
      // explicitly fetch (rather than just invalidating + relying on
      // the tab-switch effect) so the next tab opens already loaded.
      setPreview(null);
      await loadPreview();
      setTabIndex(3);
    } catch (e) {
      console.error("Failed to save payment plan", e);
      toast.error(e?.message || "Failed to save payment plan");
    } finally {
      setSavingPaymentPlan(false);
    }
  };

  // ---------- Submit ----------
  const openSubmitConfirm = () => setSubmitConfirmOpen(true);
  const closeSubmitConfirm = () => {
    if (submitting) return;
    setSubmitConfirmOpen(false);
  };
  const handleSubmit = async () => {
    if (!crId) return;
    setSubmitting(true);
    try {
      await actionProposalChangeRequest(proposalId, crId, "SUBMIT");
      toast.success("Change request submitted");
      setSubmitConfirmOpen(false);
      backToChanges();
    } catch (e) {
      console.error("Failed to submit change request", e);
      toast.error(e?.message || "Failed to submit change request");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Navigation ----------
  const backToChanges = () => {
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
    });
    navigate(`/project/details/${proposalId}/changes?${qs.toString()}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const activeTab = TABS[tabIndex]?.id || "milestone";

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: { xs: "2vh 4vw 1vh", md: "3vh 2.5vw 1.5vh" },
          gap: "10px",
          background:
            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          flexShrink: 0,
        }}
      >
        <LeftArrowIcon onClick={backToChanges} style={{ cursor: "pointer" }} />
        <Box
          sx={{
            color: "#0D0D0E",
            fontSize: { xs: "15px", md: "16px" },
            fontWeight: 500,
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          Change Request #{cr?.changeRequestId || crId}
          {cr?.status && <StatusPill status={cr.status} />}
        </Box>
      </Box>

      <Box sx={{ padding: { xs: "1vh 4vw", md: "1vh 2.5vw" }, flexShrink: 0 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => {
            if (!tabsUnlocked && v !== 0) return;
            setTabIndex(v);
          }}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: "36px",
            "& .MuiTabs-indicator": { background: "none" },
          }}
        >
          <StyledTab label="Billing Milestone" selected={tabIndex === 0} />
          <StyledTab
            label="Timeline"
            selected={tabIndex === 1}
            sx={
              !tabsUnlocked ? { cursor: "not-allowed !important" } : undefined
            }
          />
          <StyledTab
            label="Payments"
            selected={tabIndex === 2}
            sx={
              !tabsUnlocked ? { cursor: "not-allowed !important" } : undefined
            }
          />
          <StyledTab
            label="Review"
            selected={tabIndex === 3}
            sx={
              !tabsUnlocked ? { cursor: "not-allowed !important" } : undefined
            }
          />
        </Tabs>
      </Box>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: { xs: "1vh 4vw 2vh", md: "1vh 2.5vw 2vh 2.5vw" },
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          minWidth: 0,
        }}
      >
        {activeTab === "milestone" && (
          <BillingMilestoneTab
            milestones={milestones}
            editedById={editedById}
            setEditedById={setEditedById}
            selectedMilestoneIds={selectedMilestoneIds}
            onToggleMilestone={handleToggleMilestone}
            onAutoSelectMilestone={handleAutoSelectMilestone}
            syntheticEdit={syntheticEdit}
            setSyntheticEdit={setSyntheticEdit}
            crPrice={cr?.changeRequestPrice}
            readOnly={isReadOnly}
          />
        )}
        {activeTab === "timeline" && (
          <TimelineTab
            deliverables={timelineDeliverables}
            setDeliverables={setTimelineDeliverables}
          />
        )}
        {activeTab === "payments" && (
          <PaymentsTab
            crPrice={cr?.changeRequestPrice}
            lines={paymentLines}
            setLines={setPaymentLinesAndClearErrors}
            readOnly={isReadOnly}
            errors={paymentErrors}
          />
        )}
        {activeTab === "preview" && (
          <PreviewTab cr={cr} preview={preview} loading={previewLoading} />
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          padding: { xs: "12px 4vw", md: "1.5vh 2vw" },
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          borderTop: { xs: "1px solid #E5E7EB", md: "none" },
        }}
      >
        <SecondaryButton onClick={backToChanges}>Cancel</SecondaryButton>
        {activeTab === "milestone" && (
          <PrimaryButton
            onClick={
              isReadOnly ? () => setTabIndex(1) : handleSaveMilestoneAndNext
            }
            disabled={
              !isReadOnly &&
              (saving || (!anyDirty && !isSyntheticDirty))
            }
          >
            {saving ? "Saving..." : "Next"}
          </PrimaryButton>
        )}
        {activeTab === "timeline" && (
          <PrimaryButton onClick={() => setTabIndex(2)}>Next</PrimaryButton>
        )}
        {activeTab === "payments" && (
          <PrimaryButton
            onClick={handleSavePaymentPlanAndNext}
            disabled={!isReadOnly && savingPaymentPlan}
          >
            {savingPaymentPlan ? "Saving..." : "Next"}
          </PrimaryButton>
        )}
        {activeTab === "preview" &&
          String(cr?.status || "").toUpperCase() === "DRAFT" && (
            <PrimaryButton
              onClick={openSubmitConfirm}
              disabled={submitting || !preview}
            >
              {submitting ? "Submitting..." : "Submit"}
            </PrimaryButton>
          )}
      </Box>

      <SubmitChangeRequestModal
        open={submitConfirmOpen}
        onClose={closeSubmitConfirm}
        onConfirm={handleSubmit}
        submitting={submitting}
      />
    </Box>
  );
}
